import React, { useState } from "react";
import { Alert, Box, Stack, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import Button from "../button";
import TextInput from "../inputs/TextInput";
import StudentResultList from "./StudentResultList";
import { useSchoolFeeStudentSearch } from "../../api/queries";
import { useSchoolFee } from "../../context/schoolFeeContext";
import { getFailureDisplay, getStorageToken } from "../../utils";
import { SchoolFeeStudentResult } from "../../models/schoolFee";

// The server answers a `name` shorter than this with an empty HTTP 200 rather
// than a validation error, so without the same minimum on the client a payer
// cannot tell "too short to search" from "no such student". It is stated on
// screen for the same reason.
export const MIN_NAME_LENGTH = 3;

type Criteria = { matricule?: string; name?: string };

/**
 * Journey A's identity step: find the student on the school's roster.
 *
 * It owns the search rather than the template because the RESULT is what
 * advances the journey -- there is no Continue button here to press early.
 */
const StudentSearchForm = ({ coreServiceId }: { coreServiceId: string }) => {
  const { t, i18n } = useTranslation();
  const { selectStudent } = useSchoolFee();
  const token = getStorageToken();

  const [matricule, setMatricule] = useState("");
  const [name, setName] = useState("");
  // The criteria a search was actually RUN with, held apart from what is in
  // the fields: typing after a search must not silently re-key the query and
  // replace the list the payer is reading.
  const [criteria, setCriteria] = useState<Criteria | null>(null);
  // Set by a Search press with nothing usable, cleared by the next keystroke.
  const [refused, setRefused] = useState(false);

  const nameTooShort =
    name.trim() !== "" && name.trim().length < MIN_NAME_LENGTH;
  const canSearch =
    matricule.trim() !== "" || name.trim().length >= MIN_NAME_LENGTH;

  const search = useSchoolFeeStudentSearch({
    coreServiceId,
    matricule: criteria?.matricule,
    name: criteria?.name,
    authorisation: token || "",
    enabled: !!criteria,
  });

  const onSubmit = () => {
    if (!canSearch) {
      setRefused(true);
      return;
    }

    // Matricule is the precise identifier, so it wins when both are filled in;
    // sending both would have the server disambiguate a choice the payer
    // already made by typing the number.
    setCriteria(
      matricule.trim() !== ""
        ? { matricule: matricule.trim() }
        : { name: name.trim() }
    );
  };

  // Selecting is the whole of this step's output: the context writes the
  // identity and moves the journey on in one update.
  const onSelect = (hit: SchoolFeeStudentResult) => selectStudent(hit);

  const results = search.data?.items ?? [];
  const searched = !!criteria && search.isSuccess;

  return (
    <Box mb={4} data-testid="school-fee-student-search">
      <Typography variant="h6" fontWeight="bold" mb={2}>
        {t("schoolfee.steps.identity")}
      </Typography>
      <Typography sx={{ fontSize: 13, mb: 2 }}>
        {t("schoolfee.search.description")}
      </Typography>

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={{ xs: 1, md: 2 }}
        alignItems={{ xs: "stretch", md: "flex-start" }}
      >
        <Box flex={1}>
          <TextInput
            fullWidth
            type="text"
            id="schoolfee-matricule"
            label={t("schoolfee.search.matricule_label")}
            value={matricule}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setMatricule(event.target.value);
              setRefused(false);
            }}
            placeholder={t("schoolfee.search.matricule_placeholder")}
            variant="outlined"
          />
        </Box>

        {/* The two boxes are ALTERNATIVES, not a pair to fill in, and nothing
            on screen said so -- a payer could reasonably read them as both
            required. It takes its colour from the theme rather than a literal,
            because branding is resolved per corporate group here.

            Centred on the BOXES, not on the field: each field is a 14px label
            at 1.3 line-height, its 6px gap, then the 48px input, so the boxes
            start 24px below the row's top. The OR pays that 24px as padding
            and carries the boxes' own height, which puts its text on their
            centre line; stretching instead would let the name field's helper
            text below drag it off.

            ⛔ Padding, NOT margin. Stack spaces its children with
            `& > :not(style):not(style) { margin: 0 }`, which outranks a
            margin set on the child -- a margin-top here is silently reset and
            the OR rides at the top of the row. */}
        <Typography
          data-testid="school-fee-search-or"
          color="primary.main"
          fontWeight={700}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxSizing: "border-box",
            pt: { md: "24px" },
            // height: { md: 72 },
          }}
        >
          {t("schoolfee.search.or")}
        </Typography>

        <Box flex={1}>
          <TextInput
            fullWidth
            type="text"
            id="schoolfee-name"
            label={t("schoolfee.search.name_label")}
            value={name}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setName(event.target.value);
              setRefused(false);
            }}
            error={nameTooShort}
            helperText={t("schoolfee.search.name_minimum")}
            placeholder={t("schoolfee.search.name_placeholder")}
            variant="outlined"
          />
        </Box>
      </Stack>

      {refused && !nameTooShort && (
        <Typography role="alert" color="error" sx={{ fontSize: 13, mt: 1 }}>
          {t("schoolfee.search.need_one")}
        </Typography>
      )}
      <Box mt={3}>
        {/* Primary until there is an answer on screen, then white-on-a-line:
            once the result card carries the call to action, a second solid
            brand button beside it competes with the one that matters. */}
        <Button
          variant={results.length > 0 ? "outlined" : "contained"}
          fullWidth
          size="large"
          disabled={search.isFetching}
          isLoading={search.isFetching}
          loadingMessage={t("schoolfee.search.searching")}
          startIcon={search.isFetching ? undefined : <SearchIcon />}
          // 46px and a 10px radius, as every other button in the frame.
          sx={{
            height: 46,
            borderRadius: 2.5,
            ...(results.length > 0
              ? { color: "text.primary", borderColor: "divider" }
              : {}),
          }}
          onClick={onSubmit}
        >
          {t("schoolfee.search.submit")}
        </Button>
      </Box>

      {/* No section spinner here: the Search button itself is the control the
          payer pressed, so the button is where "working" belongs. A second
          page-level spinner made the whole step look as though it had gone
          away and come back for one request. */}

      {search.isError && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {getFailureDisplay(search.error, t, i18n.language).message}
        </Alert>
      )}

      {/* D-24 / D-25: nothing found is an EMPTY RESULT rendered inline. It is
          not an error surface, it does not take the screen, and it offers no
          doorway to the catalogue -- the searchable journey is the only one
          this service has.

          Said in the theme's error red so it is not missed, in the soft brick
          tone the palette uses rather than a signal red: the message tells the
          payer how to fix the search, so it should read as an answer, not an
          alarm. */}
      {searched && results.length === 0 && (
        <Box
          mt={3}
          data-testid="school-fee-search-empty"
          sx={{ color: "error.main" }}
        >
          <Typography variant="body1" fontWeight="700">
            {t("schoolfee.search.empty_title")}
          </Typography>
          <Typography sx={{ fontSize: 13 }}>
            {t("schoolfee.search.empty_body")}
          </Typography>
        </Box>
      )}

      {searched && results.length > 0 && (
        <StudentResultList results={results} onSelect={onSelect} />
      )}
    </Box>
  );
};

export default StudentSearchForm;
